import React, { useState, useEffect } from "react";
import { getFirestore, collection, query, where, getDocs, addDoc, doc, getDoc, updateDoc } from "firebase/firestore";
import { useFirebase } from "@/contexts/FirebaseContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parse, addHours, isAfter, isBefore, isWithinInterval } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { CalendarIcon, Loader2 } from "lucide-react";
import { Enrollment } from "@/utils/firestoreService";

// Buffer time configuration - 15 minutes between lessons for instructor break
const LESSON_BUFFER_MINUTES = 15;

interface Student {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

const timeSlots = [
  "07:00", "08:00", "09:00", "10:00", "11:00", "12:00", "13:00", 
  "14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00"
];

const BookOnBehalf = () => {
  const { getAllUsers, getEnrollmentsByUserId } = useFirebase();
  const { toast } = useToast();
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<string>("");
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const [selectedEnrollment, setSelectedEnrollment] = useState<Enrollment | null>(null);

  // Fetch all students
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const users = await getAllUsers();
        const studentUsers = users
          .filter(user => user.role === 'student')
          .map(user => ({
            id: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            role: user.role
          }));
        setStudents(studentUsers);
      } catch (error) {
        console.error("Error fetching students:", error);
        toast({
          title: "Error",
          description: "Failed to load students",
          variant: "destructive",
        });
      }
    };

    fetchStudents();
  }, []);

  // Fetch enrollments when student is selected
  useEffect(() => {
    const fetchEnrollments = async () => {
      if (!selectedStudent) {
        setEnrollments([]);
        setSelectedCourse("");
        return;
      }

      try {
        const studentEnrollments = await getEnrollmentsByUserId(selectedStudent);
        // Only show assigned enrollments
        const assignedEnrollments = studentEnrollments.filter(
          enrollment => enrollment.status === 'assigned'
        );
        setEnrollments(assignedEnrollments);
      } catch (error) {
        console.error("Error fetching enrollments:", error);
        toast({
          title: "Error",
          description: "Failed to load student enrollments",
          variant: "destructive",
        });
      }
    };

    fetchEnrollments();
  }, [selectedStudent]);

  // Check availability when date or course changes
  useEffect(() => {
    if (selectedDate && selectedCourse) {
      checkAvailability();
    } else {
      setAvailableTimeSlots([]);
    }
  }, [selectedDate, selectedCourse]);

  const checkAvailability = async () => {
    if (!selectedDate || !selectedCourse) {
      setAvailableTimeSlots([]);
      return;
    }

    setCheckingAvailability(true);
    try {
      const enrollment = enrollments.find(e => e.courseId === selectedCourse);
      if (!enrollment || !enrollment.instructorId || !enrollment.instructorEmail) {
        throw new Error("Instructor information not found");
      }

      const db = getFirestore();
      const formattedDate = format(selectedDate, "yyyy-MM-dd");

      console.log("=== ADMIN BOOKING AVAILABILITY CHECK ===");
      console.log("Checking availability for instructor:", enrollment.instructorEmail);
      console.log("Date:", formattedDate);

      // Get instructor's availability
      const availabilityDoc = await getDoc(doc(db, "instructor_availability", enrollment.instructorEmail));
      const unavailableSlots = availabilityDoc.exists() 
        ? availabilityDoc.data()?.unavailableSlots || []
        : [];

      console.log("All unavailable slots:", unavailableSlots);

      // Check if the entire day is marked as unavailable
      const isFullDayUnavailable = unavailableSlots.some((slot: any) => 
        slot && 
        typeof slot === 'object' && 
        slot.date === formattedDate && 
        slot.isFullDay
      );

      if (isFullDayUnavailable) {
        console.log("Instructor is unavailable for the entire day");
        setAvailableTimeSlots([]);
        toast({
          title: "No Availability",
          description: "The instructor is not available on this date.",
          variant: "destructive",
        });
        return;
      }

      // Get instructor's existing bookings
      const schedulesQuery = query(
        collection(db, "schedules"),
        where("date", "==", formattedDate),
        where("instructorId", "==", enrollment.instructorId),
        where("status", "in", ["pending", "confirmed"])
      );

      const schedulesSnapshot = await getDocs(schedulesQuery);
      const bookedSlots = schedulesSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          startTime: data.startTime || data.time,
          duration: data.duration || 1
        };
      });

      console.log("Instructor's booked slots:", bookedSlots);

      // Filter available time slots
      const available = timeSlots.filter(time => {
        const currentTime = parse(time, "HH:mm", new Date());
        const currentEnd = addHours(currentTime, 1); // 1 hour duration

        // Check if the time slot overlaps with instructor's unavailable times
        const isUnavailable = unavailableSlots.some((slot: any) => {
          if (!slot || typeof slot !== 'object' || !slot.date || slot.date !== formattedDate) {
            return false;
          }

          // Handle full day unavailability
          if (slot.isFullDay) {
            console.log("Time slot", time, "blocked by full day unavailability");
            return true;
          }

          // Handle time range unavailability
          if (slot.startTime && slot.endTime) {
            try {
              const slotStart = parse(slot.startTime, "HH:mm", new Date());
              const slotEnd = parse(slot.endTime, "HH:mm", new Date());
              
              const hasOverlap = (
                (currentTime >= slotStart && currentTime < slotEnd) ||
                (currentEnd > slotStart && currentEnd <= slotEnd) ||
                (currentTime <= slotStart && currentEnd >= slotEnd)
              );

              if (hasOverlap) {
                console.log("Time slot", time, "overlaps with instructor unavailability:", slot.startTime, "-", slot.endTime);
              }

              return hasOverlap;
            } catch (error) {
              console.error("Error parsing slot time:", error);
              return false;
            }
          }

          return false;
        });

        if (isUnavailable) {
          console.log("Time slot", time, "is unavailable");
          return false;
        }

        // Check if the time slot overlaps with any booked slots
        const isBooked = bookedSlots.some(slot => {
          try {
            const bookedTime = parse(slot.startTime, "HH:mm", new Date());
            const bookedEnd = addHours(bookedTime, slot.duration);
            // Add buffer time for instructor break - 15 minutes after each lesson
            const bookedEndWithBuffer = addHours(bookedEnd, LESSON_BUFFER_MINUTES / 60);
            
            const hasOverlap = (
              (currentTime >= bookedTime && currentTime < bookedEndWithBuffer) ||
              (currentEnd > bookedTime && currentEnd <= bookedEndWithBuffer) ||
              (currentTime <= bookedTime && currentEnd >= bookedEndWithBuffer)
            );

            if (hasOverlap) {
              console.log("Time slot", time, "conflicts with existing booking + buffer:", slot.startTime, "-", format(bookedEndWithBuffer, "HH:mm"));
            }

            return hasOverlap;
          } catch (error) {
            console.error("Error parsing booked time:", error);
            return false;
          }
        });

        if (isBooked) {
          console.log("Time slot", time, "is already booked");
          return false;
        }

        console.log("Time slot", time, "is available");
        return true;
      });

      console.log("Final available time slots:", available);
      setAvailableTimeSlots(available);
      
      if (available.length === 0) {
        toast({
          title: "No Available Slots",
          description: "There are no available time slots for this date.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error checking availability:", error);
      toast({
        title: "Error",
        description: "Failed to check instructor availability",
        variant: "destructive",
      });
      setAvailableTimeSlots([]);
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudent || !selectedCourse || !selectedDate || !selectedTime) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const selectedEnrollment = enrollments.find(e => e.courseId === selectedCourse);
      if (!selectedEnrollment) throw new Error("Course not found");

      const db = getFirestore();
      const schedulesRef = collection(db, "schedules");
      
      // Calculate end time (1 hour duration)
      const startTime = selectedTime;
      const endTime = format(addHours(parse(selectedTime, "HH:mm", new Date()), 1), "HH:mm");
      
      const newSchedule = {
        studentId: selectedStudent,
        enrollmentId: selectedEnrollment.id,
        courseId: selectedCourse,
        date: format(selectedDate, "yyyy-MM-dd"),
        startTime: startTime,
        endTime: endTime,
        duration: 1, // Default duration of 1 hour
        status: "pending", // Initial status is pending, waiting for instructor confirmation
        createdAt: new Date().toISOString(),
        studentName: students.find(s => s.id === selectedStudent)?.firstName + " " + students.find(s => s.id === selectedStudent)?.lastName || "Unknown Student",
        studentEmail: students.find(s => s.id === selectedStudent)?.email || "",
        courseName: selectedEnrollment.courseName,
        instructorId: selectedEnrollment.instructorId,
        instructorName: selectedEnrollment.instructorName,
        instructorEmail: selectedEnrollment.instructorEmail,
        venue: "Driving School", // Default venue
      };
      
      // Save to Firestore
      await addDoc(schedulesRef, newSchedule);
      
      toast({
        title: "Booking Created",
        description: `A new lesson has been scheduled for ${format(selectedDate, "MMMM d, yyyy")} at ${startTime}-${endTime}. Waiting for instructor confirmation.`,
      });

      // Reset form
      setSelectedStudent("");
      setSelectedCourse("");
      setSelectedDate(undefined);
      setSelectedTime("");
    } catch (error) {
      console.error("Error creating booking:", error);
      toast({
        title: "Error",
        description: "Failed to create booking",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout role="admin">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Book on Behalf</h1>
          <p className="text-gray-500 mt-2">Schedule lessons for students</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Create New Booking</CardTitle>
            <p className="text-gray-500 mt-2">Select a student and their enrolled course to schedule a lesson</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="student">Select Student</Label>
                <Select
                  value={selectedStudent}
                  onValueChange={setSelectedStudent}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a student" />
                  </SelectTrigger>
                  <SelectContent>
                    {students.map((student) => (
                      <SelectItem key={student.id} value={student.id}>
                        {student.firstName} {student.lastName} ({student.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="course">Select Course</Label>
                <Select
                  value={selectedCourse}
                  onValueChange={setSelectedCourse}
                  disabled={!selectedStudent || enrollments.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={
                      !selectedStudent 
                        ? "Select a student first" 
                        : enrollments.length === 0 
                          ? "No assigned enrollments found" 
                          : "Select a course"
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {enrollments.map((enrollment) => (
                      <SelectItem key={enrollment.courseId} value={enrollment.courseId}>
                        {enrollment.courseName} (Instructor: {enrollment.instructorName})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Select Date</Label>
                <Calendar
                  selected={selectedDate}
                  onChange={setSelectedDate}
                  minDate={new Date()}
                  maxDate={new Date(new Date().setMonth(new Date().getMonth() + 3))}
                  className="rounded-md border"
                />
              </div>

              <div className="space-y-2">
                <Label>Select Time</Label>
                <Select
                  value={selectedTime}
                  onValueChange={setSelectedTime}
                  disabled={!selectedDate || !selectedCourse || checkingAvailability || availableTimeSlots.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={
                      !selectedDate 
                        ? "Select a date first"
                        : !selectedCourse
                          ? "Select a course first"
                          : checkingAvailability
                            ? "Checking availability..."
                            : availableTimeSlots.length === 0
                              ? "No available slots"
                              : "Select a time"
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {checkingAvailability ? (
                      <div className="flex items-center justify-center py-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="ml-2">Checking availability...</span>
                      </div>
                    ) : availableTimeSlots.length === 0 ? (
                      <div className="p-2 text-center text-sm text-gray-500">
                        No available time slots
                      </div>
                    ) : (
                      availableTimeSlots.map((time) => (
                        <SelectItem key={time} value={time}>
                          {time}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <Button 
                type="submit" 
                className="w-full"
                disabled={loading || !selectedStudent || !selectedCourse || !selectedDate || !selectedTime}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Booking...
                  </>
                ) : (
                  "Create Booking"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default BookOnBehalf; 